import { Decimal, multiply } from './decimal';
/**
 * 숫자를 포맷팅 (소수점 이하 0이면 생략)
 * @param value - 포맷팅할 숫자
 * @returns 포맷팅된 문자열
 */
export function formatNumberWithoutTrailingZeros(value) {
    if (!Number.isFinite(value)) {
        return value.toString();
    }
    // 부동소수점 오차를 고려하여 정수인지 확인
    // 예: 1.0000000001 같은 경우도 정수로 간주
    const rounded = Math.round(value);
    if (Math.abs(value - rounded) < 1e-10) {
        return rounded.toString();
    }
    // 소수점 이하 0 제거
    // toFixed(10)을 사용하여 부동소수점 오차 제거 후 0 제거
    const fixed = value.toFixed(10);
    return fixed.replace(/\.?0+$/, '');
}
/**
 * 수량을 포맷팅 (천 단위 구분자 없이, 소수점 유지)
 * 금융 시스템에서 수량은 일반적으로 천 단위 구분자 없이 표기합니다.
 * 예: 1000.001 → "1000.001" (천 단위 구분자 없음)
 * 음수인 경우 '-'로 표시 (역전 상황 처리)
 *
 * @param value - 포맷팅할 수량 값
 * @param maxDecimalPlaces - 최대 소수점 자리수 (기본값: 10)
 * @returns 포맷팅된 수량 문자열
 */
export function formatQuantity(value, maxDecimalPlaces = 10) {
    if (value == null || value === '') {
        return '-';
    }
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (!Number.isFinite(numValue)) {
        return String(value);
    }
    // 음수인 경우 '-'로 표시 (역전 상황 처리)
    if (numValue < 0) {
        return '-';
    }
    // 소수점 이하 0 제거 (formatNumberWithoutTrailingZeros와 동일한 로직)
    const rounded = Math.round(numValue);
    if (Math.abs(numValue - rounded) < 1e-10) {
        return rounded.toString();
    }
    // 소수점 이하 0 제거 (천 단위 구분자 없이)
    const fixed = numValue.toFixed(maxDecimalPlaces);
    return fixed.replace(/\.?0+$/, '');
}
/**
 * orderUnit 기반 수량 옵션 생성
 * @param orderUnit - 주문 단위 수량
 * @param minOrderQuantity - 최소 주문 수량
 * @param maxOrderQuantity - 최대 주문 수량
 * @returns 수량 옵션 배열
 */
export function generateQuantityOptions(orderUnit, minOrderQuantity, maxOrderQuantity) {
    if (orderUnit <= 0) {
        return [];
    }
    const options = [];
    // 1배 ~ 9배
    for (let multiplier = 1; multiplier <= 9; multiplier++) {
        const value = multiply(orderUnit, multiplier);
        if (value >= minOrderQuantity && value <= maxOrderQuantity) {
            options.push({
                label: formatNumberWithoutTrailingZeros(value),
                value: value.toString(),
            });
        }
    }
    // 10배 ~ 90배 (10 단위)
    for (let multiplier = 10; multiplier <= 90; multiplier += 10) {
        const value = multiply(orderUnit, multiplier);
        if (value >= minOrderQuantity && value <= maxOrderQuantity) {
            options.push({
                label: formatNumberWithoutTrailingZeros(value),
                value: value.toString(),
            });
        }
    }
    // 100배 ~ 500배 (100 단위)
    for (let multiplier = 100; multiplier <= 500; multiplier += 100) {
        const value = multiply(orderUnit, multiplier);
        if (value >= minOrderQuantity && value <= maxOrderQuantity) {
            options.push({
                label: formatNumberWithoutTrailingZeros(value),
                value: value.toString(),
            });
        }
    }
    return options;
}
/**
 * orderUnit 기반 수량 조절 step 배열 생성 (단일 step용)
 * @param orderUnit - 주문 단위 수량
 * @returns step 배열 [1배, 10배]
 */
export function generateQuantityStepValues(orderUnit) {
    if (orderUnit <= 0) {
        return [0.1, 1];
    }
    return [multiply(orderUnit, 1), multiply(orderUnit, 10)];
}
/**
 * 값이 orderUnit의 배수인지 확인
 * @param value - 확인할 값
 * @param orderUnit - 주문 단위 수량
 * @returns 배수 여부
 */
export function isOrderUnitMultiple(value, orderUnit) {
    if (orderUnit <= 0) {
        return false;
    }
    // 부동소수점 오차를 고려한 비교
    const remainder = Math.abs(value % orderUnit);
    const tolerance = orderUnit * 1e-10; // 매우 작은 오차 허용
    return remainder < tolerance || remainder > orderUnit - tolerance;
}
/**
 * 값을 orderUnit 배수로 반올림 (아래 방향)
 * 부동소수점 오차를 방지하기 위해 Decimal을 사용합니다.
 * @param value - 반올림할 값
 * @param orderUnit - 주문 단위 수량
 * @returns 반올림된 값
 */
export function roundDownToOrderUnitMultiple(value, orderUnit) {
    if (orderUnit <= 0) {
        return value;
    }
    const multiplier = new Decimal(value).dividedBy(orderUnit).floor().toNumber();
    return multiply(orderUnit, multiplier);
}
/**
 * 값이 주문 가능 범위 내에 있는지 확인하고 보정
 * @param value - 확인할 값
 * @param orderUnit - 주문 단위 수량
 * @param minOrderQuantity - 최소 주문 수량
 * @param maxOrderQuantity - 최대 주문 수량
 * @returns 보정된 값
 */
export function clampToValidQuantity(value, orderUnit, minOrderQuantity, maxOrderQuantity) {
    // 1. orderUnit 배수로 반올림 (아래 방향)
    let corrected = roundDownToOrderUnitMultiple(value, orderUnit);
    // 2. 최소값 보정
    if (corrected < minOrderQuantity) {
        corrected = minOrderQuantity;
    }
    // 3. 최대값 보정
    if (corrected > maxOrderQuantity) {
        corrected = maxOrderQuantity;
    }
    return corrected;
}
//# sourceMappingURL=quantity.js.map